import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  triggerClassName?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  delay?: number;
}

// Lightweight, accessible tooltip (no external libs) with portal rendering to avoid clipping
const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className,
  triggerClassName,
  side = 'top',
  align = 'center',
  delay = 150,
}) => {
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState<number | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const [actualSide, setActualSide] = useState<TooltipProps['side']>(side);

  const onOpen = () => {
    if (timer) window.clearTimeout(timer);
    const t = window.setTimeout(() => setOpen(true), delay);
    setTimer(t);
  };
  const onClose = () => {
    if (timer) window.clearTimeout(timer);
    setTimer(null);
    setOpen(false);
  };

  const GAP = 8;

  const computePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tip = tooltipRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;

    let nextSide: TooltipProps['side'] = side;
    let top = 0;
    let left = 0;

    const placeHorizontal = () => {
      if (align === 'start') left = rect.left;
      else if (align === 'end') left = rect.right - tw;
      else left = rect.left + rect.width / 2 - tw / 2;
      left = Math.max(8, Math.min(left, vw - tw - 8));
    };

    const placeVertical = () => {
      if (align === 'start') top = rect.top;
      else if (align === 'end') top = rect.bottom - th;
      else top = rect.top + rect.height / 2 - th / 2;
      top = Math.max(8, Math.min(top, vh - th - 8));
    };

    if (side === 'top') {
      top = rect.top - th - GAP;
      placeHorizontal();
      if (top < 4) {
        nextSide = 'bottom';
        top = rect.bottom + GAP;
        placeHorizontal();
      }
    } else if (side === 'bottom') {
      top = rect.bottom + GAP;
      placeHorizontal();
      if (top + th > vh - 4) {
        nextSide = 'top';
        top = rect.top - th - GAP;
        placeHorizontal();
      }
    } else if (side === 'left') {
      left = rect.left - tw - GAP;
      placeVertical();
      if (left < 4) {
        nextSide = 'right';
        left = rect.right + GAP;
        placeVertical();
      }
    } else if (side === 'right') {
      left = rect.right + GAP;
      placeVertical();
      if (left + tw > vw - 4) {
        nextSide = 'left';
        left = rect.left - tw - GAP;
        placeVertical();
      }
    }

    setActualSide(nextSide);
    setCoords({ top: Math.round(top), left: Math.round(left) });
  };

  useEffect(() => {
    if (!open) return;
    computePosition();
    const handle = () => computePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, align]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [timer]);

  return (
    <span
      className={cn('relative inline-flex', triggerClassName)}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocus={onOpen}
      onBlur={onClose}
      ref={triggerRef}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'pointer-events-none fixed z-[9999] rounded-lg bg-neutral-900 text-white text-xs leading-tight px-3 py-2 shadow-soft-lg border border-neutral-800 animate-fade-in',
              'whitespace-nowrap font-medium',
              className
            )}
            style={coords ? { top: coords.top, left: coords.left } : undefined}
            role="tooltip"
          >
            {/* Arrow */}
            <div
              className={cn(
                'absolute w-0 h-0 border-[5px] border-transparent',
                actualSide === 'top' &&
                  'bottom-[-10px] left-1/2 -translate-x-1/2 border-t-neutral-900',
                actualSide === 'bottom' &&
                  'top-[-10px] left-1/2 -translate-x-1/2 border-b-neutral-900',
                actualSide === 'left' &&
                  'right-[-10px] top-1/2 -translate-y-1/2 border-l-neutral-900',
                actualSide === 'right' &&
                  'left-[-10px] top-1/2 -translate-y-1/2 border-r-neutral-900'
              )}
            />
            {content}
          </div>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
